import type { RouteLocationNormalized, RouteLocationRaw, Router } from 'vue-router';

import { isIsoDate, isUuid } from '@/shared/lib/validators';

export interface PushBookingTarget {
  kind: 'push-booking';
  date: string;
  organizationId: string;
  locationId: string;
  /** `group_id` записи. Отсутствует у push старого SW и у ссылки на день. */
  bookingId?: string;
}

export interface PushMessengerTarget {
  kind: 'push-messenger';
  conversationId: string;
  organizationId: string;
}

export type PushReturnTarget = PushBookingTarget | PushMessengerTarget;

export interface ReturnIntentStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface PushReturnIntentManager {
  capture(route: Pick<RouteLocationNormalized, 'name' | 'params' | 'query'>): boolean;
  take(): PushReturnTarget | null;
  clear(): void;
}

const STORAGE_KEY = 'mors:return-intent';

const singleRouteValue = (value: unknown): string | null => (typeof value === 'string' ? value : null);

export const parsePushBookingTarget = (input: {
  date: unknown;
  organizationId: unknown;
  locationId: unknown;
  bookingId?: unknown;
}): PushBookingTarget | null => {
  const date = singleRouteValue(input.date);
  const organizationId = singleRouteValue(input.organizationId);
  const locationId = singleRouteValue(input.locationId);
  const bookingId = singleRouteValue(input.bookingId);

  if (!isIsoDate(date) || !isUuid(organizationId) || !isUuid(locationId)) {
    return null;
  }

  // Невалидный bookingId не отклоняет target: день всё равно откроется.
  return {
    kind: 'push-booking',
    date,
    organizationId,
    locationId,
    ...(isUuid(bookingId) ? { bookingId } : {}),
  };
};

export const pushBookingTargetFromRoute = (
  route: Pick<RouteLocationNormalized, 'name' | 'params' | 'query'>,
): PushBookingTarget | null => {
  if (route.name !== 'push.booking') {
    return null;
  }

  return parsePushBookingTarget({
    date: route.params.date,
    organizationId: route.query.organizationId,
    locationId: route.query.locationId,
    bookingId: route.query.bookingId,
  });
};

/** Ссылка на запись в шахматке: `/booking-chessboard/:date/:bookingId?organizationId&locationId`. */
export const chessboardBookingTargetFromRoute = (
  route: Pick<RouteLocationNormalized, 'name' | 'params' | 'query'>,
): PushBookingTarget | null => {
  if (route.name !== 'booking.chessboard') {
    return null;
  }

  return parsePushBookingTarget({
    date: route.params.date,
    organizationId: route.query.organizationId,
    locationId: route.query.locationId,
    bookingId: route.params.bookingId,
  });
};

export const pushBookingTargetToRoute = (target: PushBookingTarget): RouteLocationRaw => ({
  name: 'push.booking',
  params: { date: target.date },
  query: {
    organizationId: target.organizationId,
    locationId: target.locationId,
    ...(target.bookingId ? { bookingId: target.bookingId } : {}),
  },
});

/**
 * Ссылка на запись чужой организации или локации уходит в `push.booking`:
 * там переключение локации, проверка `bookings.view` и очистка Apollo.
 *
 * ponytail: `beforeEnter` не срабатывает при переходе внутри маршрута (шахматка → шахматка
 * чужой локации). Из UI такой переход недостижим; появится — перенести в `beforeRouteUpdate`.
 */
export const resolveChessboardTenantRedirect = (
  to: Pick<RouteLocationNormalized, 'name' | 'params' | 'query'>,
  current: { organizationId: string | null; locationId: string | null },
): RouteLocationRaw | true => {
  const target = chessboardBookingTargetFromRoute(to);
  if (!target || (target.organizationId === current.organizationId && target.locationId === current.locationId)) {
    return true;
  }

  return pushBookingTargetToRoute(target);
};

export const parsePushMessengerTarget = (input: {
  conversationId: unknown;
  organizationId: unknown;
}): PushMessengerTarget | null => {
  const conversationId = singleRouteValue(input.conversationId);
  const organizationId = singleRouteValue(input.organizationId);

  if (!isUuid(conversationId) || !isUuid(organizationId)) {
    return null;
  }

  return {
    kind: 'push-messenger',
    conversationId,
    organizationId,
  };
};

export const pushMessengerTargetFromRoute = (
  route: Pick<RouteLocationNormalized, 'name' | 'params' | 'query'>,
): PushMessengerTarget | null => {
  if (route.name !== 'push.messenger') {
    return null;
  }

  return parsePushMessengerTarget({
    conversationId: route.params.conversationId,
    organizationId: route.query.organizationId,
  });
};

export const pushMessengerTargetToRoute = (target: PushMessengerTarget): RouteLocationRaw => ({
  name: 'push.messenger',
  params: { conversationId: target.conversationId },
  query: {
    organizationId: target.organizationId,
  },
});

/** Единый реестр push-роутов: добавление нового kind — одна запись здесь. */
const PUSH_TARGET_PARSERS: Record<
  string,
  (route: Pick<RouteLocationNormalized, 'name' | 'params' | 'query'>) => PushReturnTarget | null
> = {
  'push.booking': pushBookingTargetFromRoute,
  'push.messenger': pushMessengerTargetFromRoute,
  'booking.chessboard': chessboardBookingTargetFromRoute,
};

const pushTargetToRoute = (target: PushReturnTarget): RouteLocationRaw => {
  return target.kind === 'push-booking' ? pushBookingTargetToRoute(target) : pushMessengerTargetToRoute(target);
};

export const createPushReturnIntentManager = (storage: ReturnIntentStorage): PushReturnIntentManager => ({
  capture: (route) => {
    const parser = typeof route.name === 'string' ? PUSH_TARGET_PARSERS[route.name] : undefined;
    if (!parser) {
      return false;
    }

    const target = parser(route);
    if (!target) {
      storage.removeItem(STORAGE_KEY);
      return false;
    }

    storage.setItem(STORAGE_KEY, JSON.stringify(target));
    return true;
  },
  take: () => {
    const serialized = storage.getItem(STORAGE_KEY);
    storage.removeItem(STORAGE_KEY);
    if (!serialized) {
      return null;
    }

    try {
      const parsed = JSON.parse(serialized) as Record<string, unknown>;

      if (parsed.kind === 'push-booking') {
        return parsePushBookingTarget({
          date: parsed.date,
          organizationId: parsed.organizationId,
          locationId: parsed.locationId,
          bookingId: parsed.bookingId,
        });
      }

      if (parsed.kind === 'push-messenger') {
        return parsePushMessengerTarget({
          conversationId: parsed.conversationId,
          organizationId: parsed.organizationId,
        });
      }

      return null;
    } catch {
      return null;
    }
  },
  clear: () => storage.removeItem(STORAGE_KEY),
});

const getReturnIntentManager = (): PushReturnIntentManager => {
  return createPushReturnIntentManager(sessionStorage);
};

export const capturePushReturnIntent = (route: Pick<RouteLocationNormalized, 'name' | 'params' | 'query'>): boolean =>
  getReturnIntentManager().capture(route);

export const clearPushReturnIntent = (): void => {
  getReturnIntentManager().clear();
};

export const replaceWithPushIntentOrHome = async (router: Router): Promise<void> => {
  const target = getReturnIntentManager().take();
  await router.replace(target ? pushTargetToRoute(target) : { name: 'home.index' });
};
